import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { AlertCircle, BookOpen, RefreshCw, Info } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import Shimmer from "../../Common/Shimmer";
import SyllabusView from "../../Syllabus/SyllabusView";
import SyllabusProgressHeader from "./SyllabusProgressHeader";
import StudentSyllabusDayCard from "./StudentSyllabusDayCard";
import ModuleDivider from "../../Syllabus/schedule/ModuleDivider";
import "./studentSyllabus.scss";

/**
 * StudentSyllabus
 *
 * Read-only syllabus for students. Three access patterns:
 *   1. /s/batch/:batchId/syllabus  — batchId from URL
 *   2. /s/syllabus                 — auto-resolve via student profile
 *   3. embedded in /s/learning     — batchId passed as a prop (`compact`)
 *
 * Two sources, in priority order:
 *   • BatchSyllabusSession rows (`/syllabus/batch/:id/syllabus`) — the LIVE
 *     timeline: per-day status, planned dates, topic recordings.
 *   • CourseSyllabusTemplate (`/syllabus/template/:courseId`) — the static
 *     course plan, rendered through the shared <SyllabusView/>.
 *
 * The fallback is load-bearing, not a nicety. Batch sessions exist only after
 * an admin runs "Start Batch" against a *day-format* template. A module-format
 * template is never cloned into sessions at all, and a batch that was already
 * Ongoing when its template was authored can no longer be started. In each of
 * those cases the course visibly has a syllabus that the student would never
 * see. Falling back to the template covers all of them, which is what lets the
 * empty state below mean "this course has no syllabus" rather than "the admin
 * pressed the buttons in the wrong order".
 *
 * The courseId comes from the batch response (`GET /batches/:id` populates
 * `course`), so every entry point gets the fallback without a new prop.
 */
// Mirrors <SyllabusView/>'s own "nothing meaningful to render" test.
const hasSyllabusContent = (tpl) =>
  !!tpl &&
  (tpl.syllabusType === "module"
    ? (tpl.modules || []).length > 0
    : (tpl.days || []).length > 0);

const StudentSyllabus = ({ batchId: propBatchId, compact = false }) => {
  const { batchId: urlBatchId } = useParams();

  const [batchId, setBatchId] = useState(propBatchId || urlBatchId || null);
  const [batchData, setBatchData] = useState(null);
  const [syllabusData, setSyllabusData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Track which cards are expanded — auto-expand current day
  const [expandedDays, setExpandedDays] = useState(new Set());

  // ── Resolve batchId if not in URL or props ──
  useEffect(() => {
    if (propBatchId) {
      setBatchId(propBatchId);
      return;
    }
    if (urlBatchId) {
      setBatchId(urlBatchId);
      return;
    }
    // Fetch student profile to get batch
    const resolveFromStudent = async () => {
      try {
        const user = getCurrentUser();
        const studentId = user?.studentId;
        if (!studentId) {
          setError("Could not determine your batch. Please contact support.");
          setLoading(false);
          return;
        }
        const { data } = await apiClient.get(`/students/${studentId}`);
        const batch = data.student?.batch;
        if (batch?._id) {
          setBatchId(batch._id);
        } else {
          setError("You are not enrolled in any batch yet.");
          setLoading(false);
        }
      } catch {
        setError("Failed to load your profile.");
        setLoading(false);
      }
    };
    resolveFromStudent();
  }, [propBatchId, urlBatchId]);

  // ── Fetch syllabus ──
  const fetchSyllabus = useCallback(
    async () => {
      if (!batchId) return;
      setLoading(true);
      setError("");
      setSyllabusData(null);
      setTemplate(null);

      // ── 1. Batch — also yields the courseId for the template fallback ──
      let batch = null;
      try {
        const batchRes = await apiClient.get(`/batches/${batchId}`);
        batch = batchRes.data.batch || batchRes.data;
        setBatchData(batch);
      } catch (err) {
        setError(
          err?.response?.status === 404
            ? "Batch not found."
            : err?.response?.data?.message || "Failed to load your batch."
        );
        setLoading(false);
        return;
      }

      // ── 2. Live batch sessions ──
      let sessions = null;
      let sessionErr = "";
      try {
        const { data } = await apiClient.get(
          `/syllabus/batch/${batchId}/syllabus`
        );
        if (data?.sessions?.length) {
          setSyllabusData(data);
          sessions = data.sessions;

          // Auto-expand current day (first non-completed)
          const currentDay = data.sessions.find((s) => s.status !== "completed");
          if (currentDay) setExpandedDays(new Set([currentDay.dayNumber]));
        }
      } catch (err) {
        // A 404 here is ordinary — the batch was never started. Only a real
        // server fault is worth surfacing, and only if the template also fails.
        if (err?.response?.status !== 404) {
          sessionErr = err?.response?.data?.message || "Failed to load syllabus.";
        }
      }

      // ── 3. Fallback: the course-level template ──
      if (!sessions) {
        const courseId = batch?.course?._id || batch?.course || null;
        if (courseId) {
          try {
            const { data } = await apiClient.get(`/syllabus/template/${courseId}`);
            // <SyllabusView/> renders null for a template with no days and no
            // modules, which would leave the note floating above nothing.
            // Treat that as "no template" so the empty state shows instead.
            if (hasSyllabusContent(data?.template)) setTemplate(data.template);
          } catch {
            /* no template either — the empty state below is the honest answer */
          }
        }
        if (sessionErr) setError(sessionErr);
      }

      setLoading(false);
    },
    [batchId]
  );

  useEffect(() => {
    if (batchId) fetchSyllabus();
  }, [batchId, fetchSyllabus]);

  // ── Determine current day ──
  const currentDayNumber =
    syllabusData?.sessions?.find((s) => s.status !== "completed")
      ?.dayNumber ?? null;

  // ── Toggle expand/collapse ──
  const toggleExpand = (dayNumber) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) next.delete(dayNumber);
      else next.add(dayNumber);
      return next;
    });
  };

  // Embedded in the /s/learning tab strip the tab already reads "Syllabus",
  // so the page heading would just be a second copy of the label.
  const pageHeader = compact ? null : (
    <div className="ss-header-top">
      <h1>Course Syllabus</h1>
      <p>Track your learning progress</p>
    </div>
  );

  // ── Loading state (shimmer mirroring the timeline, per project convention) ──
  if (loading) {
    return (
      <div className="student-syllabus-page">
        {pageHeader}
        <div className="ss-skeleton">
          <Shimmer height={88} />
          <Shimmer height={72} count={4} />
        </div>
      </div>
    );
  }

  // ── Error state (with retry) — only when there is nothing at all to show ──
  if (error && !syllabusData && !template) {
    return (
      <div className="student-syllabus-page">
        {pageHeader}
        <Alert variant="danger" className="ss-error-alert">
          <AlertCircle size={18} className="me-2" />
          {error}
          <button
            className="btn btn-outline-danger btn-sm ss-retry-btn"
            onClick={fetchSyllabus}
          >
            <RefreshCw size={14} className="me-1" />
            Retry
          </button>
        </Alert>
      </div>
    );
  }

  // ── Fallback: the static course plan (no live batch sessions) ──
  if (!syllabusData && template) {
    return (
      <div className="student-syllabus-page">
        {pageHeader}
        <div className="ss-plan-note">
          <Info size={16} />
          <span>
            This is the course plan. Day-by-day progress and session recordings
            appear here once your batch schedule goes live.
          </span>
        </div>
        <SyllabusView template={template} />
      </div>
    );
  }

  // ── Empty state (no sessions and no template) ──
  if (!syllabusData) {
    return (
      <div className="student-syllabus-page">
        {pageHeader}
        <div className="ss-empty-state">
          <div className="ss-empty-icon">
            <BookOpen size={32} />
          </div>
          <h3>Syllabus has not been published for this course yet.</h3>
          <p>
            Your instructor will set up the syllabus timeline once the batch
            begins. Check back later!
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="student-syllabus-page">
      {/* Progress header */}
      <SyllabusProgressHeader
        totalDays={syllabusData.totalDays}
        completedDays={syllabusData.completedDays}
        progressPercent={syllabusData.progressPercent}
        unit={syllabusData.sessions.some((s) => s.kind === "class") ? "class" : "day"}
      />

      {/* Day cards timeline */}
      <div className="ss-timeline">
        {syllabusData.sessions.map((session, i) => {
          const prev = syllabusData.sessions[i - 1];
          const startsModule = !!session.moduleTitle && (!prev || prev.moduleNumber !== session.moduleNumber);
          return (
          <React.Fragment key={session.dayNumber}>
          {startsModule && <ModuleDivider number={session.moduleNumber} title={session.moduleTitle} />}
          <StudentSyllabusDayCard
            session={session}
            batchId={batchId}
            isCurrent={session.dayNumber === currentDayNumber}
            isExpanded={expandedDays.has(session.dayNumber)}
            onToggleExpand={() => toggleExpand(session.dayNumber)}
            batchTiming={{
              startTime: batchData?.startTime,
              endTime: batchData?.endTime,
            }}
          />
          </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default StudentSyllabus;
